import { EQUATION_CONSTANTS } from "./constants";

const negativeSymbol = "!";

const operators = {
    inBetween: {
        label: "inBetween",
        precedence: 6,
        associativity: EQUATION_CONSTANTS.right,
        parameters: 3,
        type: EQUATION_CONSTANTS.function,
        evaluate: (a, p1, p2) => {
            if (p1 <= a && a <= p2) return a;
            return Math.abs(p1 - a) < Math.abs(p2 - a) ? p1 : p2;
        },
    },
    min: {
        label: "min",
        precedence: 6,
        associativity: EQUATION_CONSTANTS.right,
        parameters: 2,
        type: EQUATION_CONSTANTS.function,
        evaluate: (p1, p2) => Math.min(p2, p1),
    },
    max: {
        label: "max",
        precedence: 6,
        associativity: EQUATION_CONSTANTS.right,
        parameters: 2,
        type: EQUATION_CONSTANTS.function,
        evaluate: (p1, p2) => Math.max(p2, p1),
    },
    [negativeSymbol]: {
        label: negativeSymbol,
        precedence: 5,
        associativity: EQUATION_CONSTANTS.right,
        parameters: 1,
        type: EQUATION_CONSTANTS.sign,
        evaluate: (p1) => 0 - p1,
    },
    "^": {
        label: "^",
        precedence: 5,
        associativity: EQUATION_CONSTANTS.right,
        parameters: 2,
        type: EQUATION_CONSTANTS.binary,
        evaluate: (p1, p2) => Math.pow(p2, p1),
    },
    "/": {
        label: "/",
        precedence: 3,
        associativity: EQUATION_CONSTANTS.left,
        parameters: 2,
        type: EQUATION_CONSTANTS.binary,
        evaluate: (p1, p2) => p2 / p1,
    },
    "*": {
        label: "*",
        precedence: 3,
        associativity: EQUATION_CONSTANTS.left,
        parameters: 2,
        type: EQUATION_CONSTANTS.binary,
        evaluate: (p1, p2) => p2 * p1,
    },
    "+": {
        label: "+",
        precedence: 2,
        associativity: EQUATION_CONSTANTS.left,
        parameters: 2,
        type: EQUATION_CONSTANTS.binary,
        evaluate: (p1, p2) => p2 + p1,
    },
    "-": {
        label: "-",
        precedence: 2,
        associativity: EQUATION_CONSTANTS.left,
        parameters: 2,
        type: EQUATION_CONSTANTS.binary,
        evaluate: (p1, p2) => p2 - p1,
    },
};

String.prototype.isOperator = function () {
    return Object.keys(operators).includes(this);
};

String.prototype.isFunction = function () {
    return Object.values(operators)
        .filter((value) => value.type === EQUATION_CONSTANTS.function)
        .map((value) => value.label)
        .includes(this);
};

String.prototype.isNumeric = function () {
    return !isNaN(parseFloat(this)) && isFinite(this);
};

Array.prototype.clean = function () {
    for (let i = 0; i < this.length; i++) {
        if (this[i] === "") {
            this.splice(i, 1);
        }
    }
    return this;
};

String.prototype.mapUnirayNegativeSign = function () {
    let mappedInfix = "";
    for (let i = 0; i < this.length; i++) {
        if (
            this[i] === "-" &&
            (i === 0 || "(*+-/^,".indexOf(this[i - 1]) !== -1)
        )
            mappedInfix += negativeSymbol;
        else mappedInfix += this[i];
    }

    return mappedInfix;
};

const sanitizePostfix = (postfix) => {
    return postfix.split(EQUATION_CONSTANTS.emptySpace).clean();
};

const sanitizeInfix = (infix) => {
    const removeSpaceRegex = /\s+/g;
    const splitByRegex = /([+\-*,/^!()])/;

    return infix
        .replace(removeSpaceRegex, "")
        .mapUnirayNegativeSign()
        .split(splitByRegex)
        .clean();
};

const isNumbersConsecutive = (infix) => {
    const splitted = infix.split(EQUATION_CONSTANTS.emptySpace);

    for (let i = 1; i < splitted.length; i++) {
        if (splitted[i - 1].isNumeric() && splitted[i].isNumeric()) return true;
    }

    return false;
};

const isEquationBalanced = (infix) => {
    let counter = 0;

    for (let i = 0; i < infix.length; i++) {
        if (infix[i] === EQUATION_CONSTANTS.openBracket) {
            counter++;
        } else if (infix[i] === EQUATION_CONSTANTS.closingBracket) {
            if (counter < 1) return false;
            counter--;
        }
    }

    return counter < 1;
};

const getOperatorCondition = (o1, o2) => {
    //Check associativity and precedence
    //The higher the precedence the better priority (*) have higher precedence than (+)
    //If 2 operator have the same precedence then the associativity decides which one to calculate first
    return (
        o2?.isOperator() &&
        ((operators[o1].associativity === EQUATION_CONSTANTS.left &&
            operators[o1].precedence <= operators[o2].precedence) ||
            (operators[o1].associativity === EQUATION_CONSTANTS.right &&
                operators[o1].precedence < operators[o2].precedence))
    );
};

const infixToPostfix = (infix, scope) => {
    let outputQueue = "";
    let operatorStack = [];

    //Sanitize infix by removing spaces and removing empty strings after spliting by mathematical operators
    //and turning negative values to other form (-10 => u10)
    infix = sanitizeInfix(infix);

    //loop over items
    for (let i = 0; i < infix.length; i++) {
        let token = infix[i];
        //if item is number or variable add to output queue
        if (token.isNumeric() || Object.keys(scope).includes(token)) {
            outputQueue += token + EQUATION_CONSTANTS.emptySpace;
        }
        //else if item is operator
        else if (token?.isOperator()) {
            //set o1 to the selected operator
            let o1 = token;
            //set o2 to the first item in operator stack (first to leave)
            let o2 = operatorStack[operatorStack.length - 1];
            while (getOperatorCondition(o1, o2)) {
                //add operator with higher precedence/associativity first to the output queue
                outputQueue +=
                    operatorStack.pop() + EQUATION_CONSTANTS.emptySpace;
                //repeat the process by updating o2 value
                o2 = operatorStack[operatorStack.length - 1];
            }
            //push the selected operator to the operator stack anyways
            operatorStack.push(o1);
        } else if (token === EQUATION_CONSTANTS.openBracket) {
            operatorStack.push(token);
        } else if (token === EQUATION_CONSTANTS.closingBracket) {
            //Pop operators from operator stack and push them to output queue untill scope (brackets) close
            while (
                operatorStack.length > 0 &&
                operatorStack[operatorStack.length - 1] !==
                    EQUATION_CONSTANTS.openBracket
            ) {
                outputQueue +=
                    operatorStack.pop() + EQUATION_CONSTANTS.emptySpace;
            }
            operatorStack.pop();
            while (operatorStack[operatorStack?.length - 1]?.isFunction())
                outputQueue +=
                    operatorStack.pop() + EQUATION_CONSTANTS.emptySpace;
        } else if (token === EQUATION_CONSTANTS.comma) {
            while (
                operatorStack.length > 0 &&
                operatorStack[operatorStack.length - 1] !==
                    EQUATION_CONSTANTS.openBracket
            ) {
                outputQueue +=
                    operatorStack.pop() + EQUATION_CONSTANTS.emptySpace;
            }
        }
    }
    //Empty the remainder of operator stack into the output queue
    while (operatorStack.length > 0) {
        outputQueue += operatorStack.pop() + EQUATION_CONSTANTS.emptySpace;
    }

    return outputQueue;
};

const evaluatePostfix = (postfix, scope) => {
    let evaluationStack = [];

    //Sanitize postfix by splitting by spaces and then clean (remove empty indices)
    postfix = sanitizePostfix(postfix);

    //Loop over postfix to determine wether selected token in numeric or operator
    for (let i = 0; i < postfix.length; i++) {
        const token = postfix[i];

        //if numeric add to evaluation stack
        if (token.isNumeric()) {
            evaluationStack.push(token);
        }
        //else if variable is added substitute with value from scope
        else if (Object.keys(scope).includes(token)) {
            evaluationStack.push(scope[token]);
        }
        //else if operator pop parameters from evaluation stack and apply thier functionality
        //and add to evaluation stack
        else if (token.isOperator()) {
            const output = [];
            for (let j = 0; j < operators[token].parameters; j++) {
                output.push(Number(evaluationStack.pop()));
            }
            evaluationStack.push(operators[token].evaluate(...output));
        }
    }

    //return final number
    return evaluationStack[0];
};

const validateEquation = (equation, scope) => {
    if (!equation) return false;
    if (isNumbersConsecutive(equation)) return false;
    if (!isEquationBalanced(equation)) return false;
    if (isNaN(evaluatePostfix(infixToPostfix(equation, scope), scope)))
        return false;
    return true;
};

export { validateEquation };
